import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';
import debounce from 'lodash/debounce';
import times from 'lodash/times';

import Drawer from 'components/uiLibrary/Drawer';
import Checkbox from 'components/uiLibrary/Inputs/Checkbox';
import { PrimaryButton } from 'components/uiLibrary/LinkButton';
import Skeleton from 'components/uiLibrary/Loaders/Skeleton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import TextField from 'components/uiLibrary/TextField';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, GOOGLE_OLD_TRACKING_SERVICES } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';
import { AGGREGATION_TYPES, TP, URL_SLUG_TYPES, SORT_OPTION_VALUES, DATE_FORMATS } from 'constants/index';
import { CASTING_SEARCH_CATEGORIES_KEYS } from 'constants/search';

import { getComposerFromCreators } from 'utils/composer';
import { createDate } from 'utils/date';
import { getYearsCountFromOBStartingYear } from 'utils/globals';
import { useQuery } from 'utils/react-query';
import { trackSearch } from 'utils/tracking';
import { getWorkTitle } from 'utils/works';

import { transformProductionAggregationYears } from 'containers/Globals/transformers';
import searchQueries from 'containers/Search/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './SearchDrawer.module.scss';

const SearchInput = ({ value, onChange }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <TextField
      variant="outlined"
      label={inputValue ? '' : t(`${TP}.TYPE_TO_SEARCH`)}
      value={inputValue}
      onChange={e => {
        setInputValue(e.target.value);
        onChange(e.target.value);
      }}
      InputProps={{
        endAdornment: inputValue ? (
          <SpriteIcon
            icon="close"
            size={16}
            className={classes.input__clearIcon}
            onClick={() => {
              setInputValue('');
              onChange('');
            }}
          />
        ) : (
          <span className={classes.input__searchIcon}>
            <SpriteIcon icon="search" size={16} />
          </span>
        ),
      }}
      fullWidth
      className={classes.input}
      InputLabelProps={{ shrink: false }}
      inputRef={inputRef}
    />
  );
};

const SearchResultCard = ({ entity, entityType, trackingData }) => {
  if (entityType === ENTITY_TYPES.WORK) {
    const composer = getComposerFromCreators(entity?.creators);

    return (
      <span className={classes.searchDrawer__option_label}>
        <Typography size="12" weight="medium">
          <EntityName
            entity={{
              ...entity,
              name: entity?.name,
            }}
            entityType={entityType}
            trackingData={trackingData}
          />
          {entity?.original_name !== entity?.name && (
            <Typography size="10" color="quinary" className={classes.searchDrawer__option_originalName}>
              ({entity?.original_name})
            </Typography>
          )}
        </Typography>
        {composer?.profile?.name && (
          <Typography size="10" color="quinary" italic>
            <EntityName
              entity={{
                ...composer?.profile,
                name: composer?.profile?.name,
              }}
              entityType={ENTITY_TYPES.PROFILE}
              trackingData={trackingData}
            />
          </Typography>
        )}
        {entity?.workTypes?.length > 0 && (
          <Typography size="10" color="secondary" textTransform="uppercase">
            {entity.workTypes[0]?.name}
          </Typography>
        )}
      </span>
    );
  }

  if (entityType === ENTITY_TYPES.PROFILE) {
    return (
      <span className={classes.searchDrawer__option_label}>
        <Typography size="12" weight="medium">
          <EntityName
            entity={{
              ...entity,
              name: entity?.label,
            }}
            entityType={entityType}
            trackingData={trackingData}
          />
        </Typography>
        <Typography size="10" color="secondary">
          {entity?.profession?.name}
        </Typography>
      </span>
    );
  }

  return entity?.label;
};

const SearchDrawer = ({
  category,
  onClose,
  onClick,
  composer,
  setComposer,
  booleanQuery,
  workId,
  countries,
  sinceYear,
  trackingData,
}) => {
  const { t } = useTranslation('NS_CASTING_TOOL');
  const track = useTracking();
  const { aggregationType, queryParams, key, entityType } = category;
  const [query, setQuery] = useState('');
  const isSinceYear = key === CASTING_SEARCH_CATEGORIES_KEYS.SINCE;
  const isCountry = key === CASTING_SEARCH_CATEGORIES_KEYS.COUNTRIES;
  const { data: fetchedWork } = useQuery(
    searchQueries.getSelectedOption({
      identifier: workId,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        work_id: workId,
        aggregation_type: AGGREGATION_TYPES.WORK,
      },
      queryConfig: {
        enabled: !!workId,
      },
    }),
  );
  const [selectedCountries, setSelectedCountries] = useState(countries?.length > 0 ? countries : []);
  const composerDetails = useMemo(
    () => (composer?.id ? composer : getComposerFromCreators(fetchedWork?.creators)?.profile || {}),
    [composer, fetchedWork?.creators],
  );
  const yearLimit = useMemo(() => getYearsCountFromOBStartingYear(), []);

  const { data, isLoading } = useQuery(
    searchQueries.getDropDownList({
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        ...queryParams,
        aggregation_query: query,
        aggregation_type: aggregationType,
        ...(workId && {
          work_id: workId,
        }),
        ...(key === CASTING_SEARCH_CATEGORIES_KEYS.WORKS &&
          composerDetails?.id && {
            work_creator_id: composerDetails?.id,
          }),
        ...(key === CASTING_SEARCH_CATEGORIES_KEYS.CREATORS && {
          work_id: booleanQuery?.value?.[URL_SLUG_TYPES.WORK],
        }),
        ...(isSinceYear && {
          limit: yearLimit,
          sort: SORT_OPTION_VALUES.YEAR_DESC,
        }),
        ...(isCountry && {
          ...(sinceYear && {
            date_from: createDate(sinceYear).format(DATE_FORMATS.FULL_DATE),
          }),
          sort: SORT_OPTION_VALUES.NAME_ASC,
        }),
      },
      queryConfig: {
        keepPreviousData: true,
        select: response => {
          if (aggregationType === AGGREGATION_TYPES.YEAR) {
            return transformProductionAggregationYears(response?.data?.data);
          }

          if (key === CASTING_SEARCH_CATEGORIES_KEYS.CREATORS && composerDetails?.id) {
            return [
              {
                ...composerDetails,
                id: composerDetails?.key,
                entityType: ENTITY_TYPES.PROFILE,
                label: composerDetails?.name,
                profession: {
                  name: t(`${TP}.m_COMPOSER`),
                },
              },
            ];
          }

          return (response?.data?.data || [])?.map(item => {
            let itemLabel = item?.name;

            if (aggregationType === AGGREGATION_TYPES.WORK) {
              itemLabel = getWorkTitle(item, true, true);
            }

            return { ...item, label: itemLabel };
          });
        },
        enabled: !!aggregationType,
        onSuccess: () => {
          if (query?.length > 0) {
            track.click(trackSearch(aggregationType, query), GOOGLE_OLD_TRACKING_SERVICES);
          }
        },
      },
    }),
  );

  const handleUpdateQuery = useMemo(
    () =>
      debounce(value => {
        setQuery(value);
      }, 300),
    [],
  );

  const handleClickItem = useCallback(
    item => {
      onClick(key, item);

      if (key === CASTING_SEARCH_CATEGORIES_KEYS.CREATORS) {
        setComposer(item);
      }

      if (key === CASTING_SEARCH_CATEGORIES_KEYS.WORKS) {
        const composerInfo = getComposerFromCreators(item?.creators);
        setComposer(composerInfo?.profile);
      }

      onClose();
    },
    [onClick, key, onClose, setComposer],
  );

  const handleApply = useCallback(() => {
    onClick(key, selectedCountries);
    onClose();
  }, [onClick, key, selectedCountries, onClose]);

  return (
    <Drawer isOpen onClose={onClose} className={classes.drawer}>
      <div className={classes.drawer__header}>
        <SpriteIcon icon="arrow_back" size="16" onClick={onClose} />
        <Typography weight="bold" className={classes.drawer__header_title}>
          {t(category.label)}
        </Typography>
        <SpriteIcon icon="close" size="16" onClick={onClose} />
      </div>
      <div className={classes.searchDrawer__content}>
        {!isSinceYear && <SearchInput onChange={handleUpdateQuery} />}
        {[CASTING_SEARCH_CATEGORIES_KEYS.CREATORS, CASTING_SEARCH_CATEGORIES_KEYS.WORKS].includes(key) && (
          <Typography size="12" weight="medium" variant="p" className={classes.searchDrawer__options_title}>
            {query ? t(`${TP}.FN_ALL`) : t(`${TP}.FN_SUGGESTED`)}
          </Typography>
        )}
        <div
          className={classnames(classes.searchDrawer__options, {
            [classes.searchDrawer__options_sinceYear]: isSinceYear,
            [classes.searchDrawer__options_country]: isCountry,
          })}
        >
          {isCountry && !isLoading && (
            <div className={classes.searchDrawer__option}>
              <Checkbox
                size="small"
                label={
                  <Typography size="12" weight="medium" variant="p" className={classes.searchDrawer__option_label}>
                    {t(`${TP}.FN_ALL`)}
                  </Typography>
                }
                className={classes.searchDrawer__option_checkbox}
              />
            </div>
          )}
          {isLoading
            ? times(11, index => (
                <Skeleton key={index} variant="rectangular" height={28} className={classes.skeleton} />
              ))
            : data?.map(item => (
                <div
                  key={item.id}
                  className={classes.searchDrawer__option}
                  onClick={() => {
                    if (!isCountry) {
                      handleClickItem(item);
                    }
                  }}
                >
                  <div className={classes.searchDrawer__option_labelWrapper}>
                    {isCountry ? (
                      <Checkbox
                        size="small"
                        className={classes.searchDrawer__option_checkbox}
                        label={
                          <Typography
                            size="12"
                            weight="medium"
                            variant="p"
                            className={classes.searchDrawer__option_label}
                          >
                            <SearchResultCard
                              entity={item}
                              entityType={entityType}
                              trackingData={{
                                ...trackingData,
                                component: COMPONENTS.SEARCH_RESULT_CARD,
                              }}
                            />
                          </Typography>
                        }
                        checked={selectedCountries.some(country => country.id === item.id)}
                        onChange={() => {
                          const isSelected = selectedCountries.some(country => country.id === item.id);

                          if (isSelected) {
                            setSelectedCountries(selectedCountries.filter(country => country.id !== item.id));
                          } else {
                            setSelectedCountries([...selectedCountries, item]);
                          }
                        }}
                      />
                    ) : (
                      <Typography size="12" weight="medium" variant="p" className={classes.searchDrawer__option_label}>
                        <SearchResultCard
                          entity={item}
                          entityType={entityType}
                          trackingData={{
                            ...trackingData,
                            component: COMPONENTS.SEARCH_RESULT_CARD,
                          }}
                        />
                      </Typography>
                    )}
                  </div>
                  {!isCountry && (
                    <SpriteIcon icon="chevron_right" size="16" className={classes.searchDrawer__option_icon} />
                  )}
                </div>
              ))}
        </div>
      </div>
      {isCountry && (
        <div className={classes.searchDrawer__actions}>
          {selectedCountries?.length > 0 && (
            <Typography
              size="12"
              className={classes.searchDrawer__actions_count}
              color="quinary"
              textTransform="lowercase"
            >
              {selectedCountries?.length} {t(`${TP}.FN_SELECTED`)}
            </Typography>
          )}
          <PrimaryButton
            onClick={handleApply}
            trackingData={{
              ...trackingData,
              component: COMPONENTS.SUBMIT_CTA,
            }}
          >
            {t(`${TP}.m_APPLY`)}
          </PrimaryButton>
        </div>
      )}
    </Drawer>
  );
};

export default SearchDrawer;
